import React, { FC, ReactNode } from "react";
import { Alert, Badge, Collapse, List, Table, Typography } from "antd";
import { ExclamationCircleTwoTone, InfoCircleTwoTone } from "@ant-design/icons";
import { DataFrameImportResult, DataImportDetail } from "./data-import-form";
import { DataImportDetailType } from "../../graphql/__generated_types__/globalTypes";
import styles from "./data-import-results.css";
import { VpdScrollable } from "@design-system";
import { ProgressState } from "./reference-data/hooks/use-data-import-subscriptions";
import { ImportStateName } from "./data-import";
import { FileDataImportCompleted_fileDataImportCompleted } from "../../graphql/__generated_types__/FileDataImportCompleted";
import { DataImportResultSection } from "./data-import-result-section";

const {
  ["container"]: containerClassName,
  ["result-message"]: resultMessageClassName,
  ["scrollable"]: scrollableClassName,
  ["scrollable-content"]: scrollableContentClassName,
} = styles as any;
const { Panel } = Collapse;

export type DetailsTableProps = {
  details: DataImportDetail[];
};

type AlertProps = { type: "error" | "info"; icon: ReactNode; showIcon: boolean };
const AlertPropsMap: Record<DataImportDetailType, AlertProps> = {
  [DataImportDetailType.Error]: {
    type: "error",
    icon: <ExclamationCircleTwoTone twoToneColor={"#ff4d4f"} />, // TODO: export Icons from design system
    showIcon: true,
  },
  [DataImportDetailType.Information]: {
    type: "info",
    icon: <InfoCircleTwoTone twoToneColor={"#17a2b8"} />,
    showIcon: true,
  },
};

export const DetailsTable: FC<DetailsTableProps> = ({ details }) => {
  return (
    <Table
      columns={[
        {
          title: "Row",
          dataIndex: "rowNumber",
          key: "rowNumber",
          width: "7.5rem",
        },
        {
          title: "Messages",
          dataIndex: "type",
          key: "type",
          render: (_, { type, messages }, index) =>
            messages.map((m) => <Alert key={index} message={m} {...AlertPropsMap[type]} />),
        },
      ]}
      dataSource={details}
      rowKey="rowNumber"
      scroll={{ y: 240 }}
      pagination={false}
      locale={{ emptyText: "No details to display" }}
    />
  );
};

export type DataImportResultsProps = {
  results: DataFrameImportResult[];
  progress: ProgressState;
  importResults?: FileDataImportCompleted_fileDataImportCompleted;
  importingState: ImportStateName;
};

export const DataImportResults: FC<DataImportResultsProps> = ({
  importResults,
  results,
  progress,
  importingState,
}) => {
  const hasParseResults = results.map((x) => x.details).some((x) => x.length > 0);

  return (
    <div className={containerClassName}>
      {!hasParseResults && (
        <div>
          <DataImportResultSection
            results={results}
            progress={progress}
            importResults={importResults}
            importingState={importingState}
          />
          {importResults?.importErrors && importResults?.importErrors?.length > 0 && (
            <div className={scrollableContentClassName}>
              <List
                header={
                  <Typography.Title level={4}>
                    Error occured whilst importing {importResults.entityType}
                  </Typography.Title>
                }
                style={{ overflowY: "scroll", height: 300 }}
                size="small"
                bordered
                dataSource={importResults?.importErrors}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
            </div>
          )}
        </div>
      )}
      {results.map((x) => x.details).some((x) => x.length > 0) && (
        <VpdScrollable className={scrollableClassName}>
          <div className={scrollableContentClassName}>
            <Collapse>
              {results.map(({ succeeded, message, details }, index) => {
                const hasDetails = details.length > 0;
                if (hasDetails) {
                  return (
                    <Panel
                      key={index.toString()}
                      showArrow={hasDetails}
                      collapsible={hasDetails ? "header" : "disabled"}
                      header={
                        <Badge count={details.length}>
                          <Typography.Text
                            type={succeeded ? "success" : "danger"}
                            className={resultMessageClassName}
                          >
                            {message}
                          </Typography.Text>
                        </Badge>
                      }
                    >
                      {hasDetails && <DetailsTable details={details} />}
                    </Panel>
                  );
                }
              })}
            </Collapse>
          </div>
        </VpdScrollable>
      )}
    </div>
  );
};
